import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AccessFlowsApiAppGetAccessFlowRemediationStubRequest,
  AccessFlowsApiListGetAccessFlowRemediationRequest,
  AccessFlowsService,
  API_LIST_LIMIT,
  FrontierService,
  StandingAccessService,
} from '@api';
import {
  IntegrationResourcesFilterInput,
  PaginationInput,
  StandingAccessReportEntitlementExportSelectionInput,
  StandingAccessReportEntitlementFilterInput,
  StandingAccessReportEntitlementFragmentFragment,
} from '@api/gql/graphql';
import { useAppQuery } from '@api/query/use-app-query';

import { StandingAccessKeys } from './queryKeys';

import {
  countIntegrationResourcesDocument,
  getStandingAccessReportEntitlementLinksDocument,
  getStandingAccessReportEntitlementLinksExportDataDocument,
  getStandingAccessReportEntitlementsDocument,
  getStandingAccessReportFilterOptionsLinkedElementsDocument,
  getStandingAccessReportFilterOptionsPermissionsDocument,
  getStandingAccessReportFilterOptionsResourcesDocument,
  getStandingAccessReportFilterOptionsRiskScoresDocument,
  getStandingAccessReportTierCountsDocument,
  getStandingAccessReportUsagesDocument,
  standingAccessReportEntitlementFragmentDocument,
  standingAccessReportEntitlementLinkedElementFragmentDocument,
  standingAccessReportPermissionFragmentDocument,
  standingAccessReportResourceFragmentDocument,
} from './gql';
import { getFragmentData } from '@api/gql';

export function useListStandingAccessReports({
  enabled = true,
  refetchInterval,
}: { enabled?: boolean; refetchInterval?: number } = {}) {
  const queryClient = useQueryClient();

  const fetchMethod = async () => {
    const res = await StandingAccessService.listStandingAccessReports();

    res.data
      .sort((a, b) => {
        const aDate = a.assessed_at || a.created_date;
        const bDate = b.assessed_at || b.created_date;
        return aDate - bDate;
      })
      .forEach((report) => {
        queryClient.setQueryData([StandingAccessKeys.getReport, report.id], report);
      });

    return res.data;
  };

  const { data: reports, ...rest } = useQuery([StandingAccessKeys.listReports], fetchMethod, {
    initialData: () => [],
    enabled,
    refetchInterval,
  });

  return {
    reports,
    ...rest,
  };
}

export function useAppGetAccessFlowRemediation(args: AccessFlowsApiAppGetAccessFlowRemediationStubRequest) {
  const { data: accessFlowRemediation, ...rest } = useQuery(
    [StandingAccessKeys.getAccessFlowRemediation, JSON.stringify(args)],
    async () => AccessFlowsService.appGetAccessFlowRemediationStub(args),
  );

  return {
    accessFlowRemediation,
    ...rest,
  };
}

export function useRemediatedAccessFlows(args: AccessFlowsApiListGetAccessFlowRemediationRequest) {
  const { data: remediatedAccessFlows, ...rest } = useQuery(
    [StandingAccessKeys.getRemediatedAccessFlows, JSON.stringify(args)],
    async () => {
      const res = await AccessFlowsService.listGetAccessFlowRemediation(args);
      return res.tier_flows || {};
    },
    {
      initialData: {},
    },
  );

  return {
    remediatedAccessFlows,
    ...rest,
  };
}

export function useCountIntegrationResources(args: { id: string; filter?: IntegrationResourcesFilterInput }) {
  const [isFirstTimeFetched, setIsFirstTimeFetched] = useState(false);

  const fetch = async () => {
    const res = await FrontierService.request(countIntegrationResourcesDocument, args);
    setIsFirstTimeFetched(true);
    return res;
  };

  const res = useAppQuery([StandingAccessKeys.countResources, JSON.stringify(args)], fetch, {
    keepPreviousData: true,
  });

  return { ...res, isFetched: isFirstTimeFetched };
}

export function useGetStandingAccessReportEntitlements(args: {
  reportId: string;
  filter?: StandingAccessReportEntitlementFilterInput;
  pagination?: PaginationInput;
}) {
  const queryClient = useQueryClient();

  const [isFirstTimeFetched, setIsFirstTimeFetched] = useState(false);

  const fetch = async () => {
    const res = await FrontierService.request(getStandingAccessReportEntitlementsDocument, args);

    res.standingAccessReportEntitlements.edges.forEach((edge) => {
      queryClient.setQueryData(
        [StandingAccessKeys.getReportEntitlements, 'view', edge.cursor],
        getFragmentData(standingAccessReportEntitlementFragmentDocument, edge.node),
      );
    });

    setIsFirstTimeFetched(true);
    return res;
  };

  const res = useAppQuery([StandingAccessKeys.getReportEntitlements, JSON.stringify(args)], fetch, {
    keepPreviousData: true,
  });

  return { ...res, isFetched: isFirstTimeFetched };
}

export function useGetStandingAccessReportEntitlementGroup({ groupCursor }: { groupCursor: string }) {
  return useQuery<StandingAccessReportEntitlementFragmentFragment | undefined>(
    [StandingAccessKeys.getReportEntitlements, 'view', groupCursor],
    () => undefined,
  );
}

export function useGetStandingAccessReportUsages({
  reportId,
  groupCursor,
  filter,
  pageSize,
}: {
  reportId: string;
  groupCursor: string;
  filter?: StandingAccessReportEntitlementFilterInput;
  pageSize?: number;
}) {
  const fetch = async ({ pageParam = 0 }) => {
    const pagination = {
      take: pageSize || API_LIST_LIMIT,
      offset: pageParam,
    };

    const data = await FrontierService.request(getStandingAccessReportUsagesDocument, {
      reportId,
      groupCursor,
      filter,
      pagination,
    });

    return {
      data: data.standingAccessReportEntitlementsUsages,
      pagination,
    };
  };

  return useInfiniteQuery([StandingAccessKeys.getReportUsages, reportId, groupCursor, JSON.stringify(filter)], fetch, {
    getNextPageParam: (lastPage) => {
      const totalCount = lastPage.data.totalCount;
      const offset = lastPage.pagination.offset;
      const take = lastPage.pagination.take;

      return totalCount > offset + take ? offset + take : undefined;
    },
    keepPreviousData: true,
  });
}

export function useGetAssessmentReportResourcesFilterOptions({
  reportId,
  search,
  pageSize,
  enabled,
}: {
  reportId: string;
  search?: string;
  pageSize?: number;
  enabled?: boolean;
}) {
  const fetch = async ({ pageParam = 0 }) => {
    const pagination = {
      take: pageSize || API_LIST_LIMIT,
      offset: pageParam,
    };

    const data = await FrontierService.request(getStandingAccessReportFilterOptionsResourcesDocument, {
      reportId,
      search,
      pagination,
    });

    const resources =
      data.standingAccessReportFilterOptions.resources?.data.map((i) =>
        getFragmentData(standingAccessReportResourceFragmentDocument, i),
      ) || [];

    return {
      resources,
      hasMore: data.standingAccessReportFilterOptions.resources?.hasMore || false,
      pagination,
    };
  };

  return useInfiniteQuery([StandingAccessKeys.getReportResourcesFilterOptions, reportId, search], fetch, {
    getNextPageParam: (lastPage) => {
      const hasMore = lastPage.hasMore;
      const offset = lastPage.pagination.offset;
      const take = lastPage.pagination.take;

      return hasMore ? offset + take : undefined;
    },
    keepPreviousData: true,
    enabled,
  });
}

export function useGetAssessmentReportPermissionsFilterOptions({
  reportId,
  search,
  pageSize,
  enabled,
}: {
  reportId: string;
  search?: string;
  pageSize?: number;
  enabled?: boolean;
}) {
  const fetch = async ({ pageParam = 0 }) => {
    const pagination = {
      take: pageSize || API_LIST_LIMIT,
      offset: pageParam,
    };

    const data = await FrontierService.request(getStandingAccessReportFilterOptionsPermissionsDocument, {
      reportId,
      search,
      pagination,
    });

    const permissions =
      data.standingAccessReportFilterOptions.permissions?.data.map((i) =>
        getFragmentData(standingAccessReportPermissionFragmentDocument, i),
      ) || [];

    return {
      permissions,
      hasMore: data.standingAccessReportFilterOptions.permissions?.hasMore || false,
      pagination,
    };
  };

  return useInfiniteQuery([StandingAccessKeys.getReportPermissionsFilterOptions, reportId, search], fetch, {
    getNextPageParam: (lastPage) => {
      const hasMore = lastPage.hasMore;
      const offset = lastPage.pagination.offset;
      const take = lastPage.pagination.take;

      return hasMore ? offset + take : undefined;
    },
    keepPreviousData: true,
    enabled,
  });
}

export function useGetAssessmentReportRiskScoresFilterOptions({
  reportId,
  enabled,
}: {
  reportId: string;
  enabled?: boolean;
}) {
  const fetch = async () => {
    const data = await FrontierService.request(getStandingAccessReportFilterOptionsRiskScoresDocument, {
      reportId,
    });

    return data.standingAccessReportFilterOptions.riskScores;
  };

  const { data: riskScores, ...rest } = useQuery(
    [StandingAccessKeys.getReportRiskScoresFilterOptions, reportId],
    fetch,
    {
      initialData: () => [],
      enabled,
    },
  );

  return {
    riskScores,
    ...rest,
  };
}

export function useGetAssessmentReportLinkedElementsFilterOptions({
  reportId,
  search,
  filter,
  pageSize,
  enabled,
}: {
  reportId: string;
  search?: string;
  filter?: StandingAccessReportEntitlementFilterInput;
  pageSize?: number;
  enabled?: boolean;
}) {
  const fetch = async ({ pageParam = 0 }) => {
    const pagination = {
      take: pageSize || API_LIST_LIMIT,
      offset: pageParam,
    };

    const data = await FrontierService.request(getStandingAccessReportFilterOptionsLinkedElementsDocument, {
      reportId,
      search,
      filter,
      pagination,
    });

    const linkedElements =
      data.standingAccessReportFilterOptions.linkedElements?.data.map((i) =>
        getFragmentData(standingAccessReportEntitlementLinkedElementFragmentDocument, i),
      ) || [];

    return {
      linkedElements,
      hasMore: data.standingAccessReportFilterOptions.linkedElements?.hasMore || false,
      pagination,
    };
  };

  return useInfiniteQuery(
    [StandingAccessKeys.getReportLinkedElementsFilterOptions, reportId, JSON.stringify({ search, filter })],
    fetch,
    {
      getNextPageParam: (lastPage) => {
        const hasMore = lastPage.hasMore;
        const offset = lastPage.pagination.offset;
        const take = lastPage.pagination.take;

        return hasMore ? offset + take : undefined;
      },
      keepPreviousData: true,
      enabled,
    },
  );
}

export function useGetAssessmentReportTierCounts(reportId: string) {
  const [isFirstTimeFetched, setIsFirstTimeFetched] = useState(false);

  const fetch = async () => {
    const res = await FrontierService.request(getStandingAccessReportTierCountsDocument, { reportId });
    setIsFirstTimeFetched(true);
    return res;
  };

  const res = useAppQuery([StandingAccessKeys.getReportTierCounts, reportId], fetch, {
    keepPreviousData: true,
  });

  return { ...res, isFetched: isFirstTimeFetched };
}

export function useGetStandingAccessReportEntitlementLinks(args: {
  reportId: string;
  filter?: StandingAccessReportEntitlementFilterInput;
  pagination?: PaginationInput;
}) {
  const [isFirstTimeFetched, setIsFirstTimeFetched] = useState(false);

  const fetch = async () => {
    const res = await FrontierService.request(getStandingAccessReportEntitlementLinksDocument, args);
    setIsFirstTimeFetched(true);
    return res;
  };

  const res = useAppQuery([StandingAccessKeys.getReportEntitlementLinks, JSON.stringify(args)], fetch, {
    keepPreviousData: true,
  });

  return { ...res, isFetched: isFirstTimeFetched };
}

const fileName = 'apono_revocation_export_{ISO_DATE}.csv';

export function useDownloadStandingAccessReportEntitlementLinksCSV(args: {
  reportId: string;
  filter?: StandingAccessReportEntitlementFilterInput;
  selection?: StandingAccessReportEntitlementExportSelectionInput;
}) {
  const { mutate: downloadCSV, isLoading: isDownloadingCSV } = useMutation({
    mutationFn: async () => {
      const resp = await FrontierService.request(getStandingAccessReportEntitlementLinksExportDataDocument, args);
      const fileContent = resp.standingAccessReportEntitlementLinksExportData;

      try {
        const decodedString = atob(fileContent);
        const url = window.URL.createObjectURL(new Blob([decodedString as unknown as string]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName.replace('{ISO_DATE}', new Date().toISOString()));
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error(error);
      }
    },
  });

  return { downloadCSV, isDownloadingCSV };
}
